import { useSelector } from '@xstate/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UserContext } from '~/app';
import {
  FEE_AUTHORIZATION_REQUIRED_MESSAGE,
  FEE_SCHEDULE_CHANGED_MESSAGE,
} from '~/features/market/listing-search/errors';
import { ResponseNegotiationModal } from '~/features/market/negotiations/ResponseNegotiationModal';
import { ActivationFeeDialog } from '~/features/market/negotiations/sub-components/ActivationFeeDialog';
import { useB2BAcceptFee } from '~/features/market/useB2BAcceptFee';
import { useDeepLinkedResponse } from '~/features/shared/deepLinkParams';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
  MarketWorkspaceGrid,
} from '~/features/shared/market-content';
import {
  ResponseUnmaskPanel,
  selectUnmaskViewerScope,
} from '~/features/shared/market-content/ResponseUnmaskPanel';
import { OpportunitiesSidebar } from '~/features/shared/OpportunitiesSidebar';
import { OpportunityRecoveryNotice } from '~/features/shared/OpportunityRecoveryNotice';
import { B2BInquiryApplyFlow } from './B2BInquiryApplyFlow';
import { OpportunityCard } from './sub-components/OpportunityCard';
import { OpportunityDetail } from './sub-components/OpportunityDetail';
import { ResponseDetail } from './sub-components/ResponseDetail';
import type { B2BResponsesSide } from './types';
import { useJobOpportunitiesService } from './useJobOpportunitiesService';
import { isListingFilled } from './utils';

/**
 * Employer B2B Opportunities page (three-panel).
 * Sidebar lists outbound MSAAS JobResponses.
 * Center shows the listing summary + negotiation history strip
 * (full chain lazy-loaded on demand).
 * Right shows listing-owner identity + messages + actions.
 */
export const JobOpportunities = ({
  title,
  side,
}: {
  title?: string;
  /** [MCV-1889] Which Buying | Selling tab to list; omitted, the rows the page always showed. */
  side?: B2BResponsesSide;
}) => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  return (
    <ScopedJobOpportunities
      key={viewerScopeKey}
      viewerScopeKey={viewerScopeKey}
      title={title}
      side={side}
    />
  );
};

const ScopedJobOpportunities = ({
  viewerScopeKey,
  title,
  side,
}: {
  viewerScopeKey: string;
  title?: string;
  side?: B2BResponsesSide;
}) => {
  const [negotiationResponseId, setNegotiationResponseId] = useState<number | null>(null);
  const [negotiationStartDraftIfEmpty, setNegotiationStartDraftIfEmpty] = useState(false);
  // [MCV-1889] DD22: below 1360px the list and the opened response are separate panes. A row the
  // user opens, or a notification / deep link to one, shows its pane; the first row selected on
  // load does not.
  const { responseId: deepLinkedResponseId, navigationKey } = useDeepLinkedResponse();
  const [detailOpen, setDetailOpen] = useState(deepLinkedResponseId != null);
  const lastNavigationRef = useRef(navigationKey);
  useEffect(() => {
    if (deepLinkedResponseId == null || navigationKey === lastNavigationRef.current) return;
    lastNavigationRef.current = navigationKey;
    setDetailOpen(true);
  }, [deepLinkedResponseId, navigationKey]);
  const {
    userInfoId,
    opportunities,
    selectedOpportunity,
    messages,
    filter,
    includeDeclined,
    loading,
    messagesLoading,
    sendingMessage,
    writing,
    uncertainAction,
    retainedReply,
    replyError,
    handleEditReply,
    sendReceipt,
    canAcknowledgeUncertainty,
    handleReviewUncertainty,
    handleAcknowledgeUncertainty,
    handleDiscardUncertainty,
    error,
    feeRefusal,
    consumeFeeRefusal,
    handleSelect: selectOpportunity,
    handleFilter,
    handleIncludeDeclined,
    handleSendMessage,
    handleAccept,
    handleAcceptWithFee,
    handleWithdraw,
    handleReload,
  } = useJobOpportunitiesService(side);
  const handleSelect = useCallback(
    (responseId: number) => {
      selectOpportunity(responseId);
      setDetailOpen(true);
    },
    [selectOpportunity]
  );
  // [MCV-1889] On a sell-side listing the responder is the buyer, who pays the activation fee: their
  // Accept previews it and shows the fee dialog first, as the Received list does. Anywhere else the
  // responder is the supplier, never billed, and accepts straight away as before.
  const acceptFee = useB2BAcceptFee({
    selectedResponseId: selectedOpportunity?.responseId ?? null,
    onAccept: (_payload, feeAuthorization) => handleAcceptWithFee(feeAuthorization),
  });
  const onAccept = useCallback(() => {
    if (selectedOpportunity?.viewerRole === 'BUYER') {
      void acceptFee.requestAccept(selectedOpportunity.responseId, undefined);
      return;
    }
    handleAccept();
  }, [selectedOpportunity, acceptFee.requestAccept, handleAccept]);
  // [MCV-1858] The server refused the accept for its platform-fee schedule (428 not authorized, 409
  // changed since the preview): show the refetched schedule again with the reason, as Received does.
  const selectedResponseId = selectedOpportunity?.responseId;
  const { requestAccept } = acceptFee;
  useEffect(() => {
    if (!feeRefusal || selectedResponseId == null) return;
    consumeFeeRefusal();
    void requestAccept(
      selectedResponseId,
      undefined,
      feeRefusal === 'FEE_SCHEDULE_CHANGED'
        ? FEE_SCHEDULE_CHANGED_MESSAGE
        : FEE_AUTHORIZATION_REQUIRED_MESSAGE
    );
  }, [feeRefusal, selectedResponseId, consumeFeeRefusal, requestAccept]);
  const negotiationOpportunity = opportunities.find(
    (opportunity) => opportunity.responseId === negotiationResponseId
  );
  const actionsBlocked =
    loading || messagesLoading || writing || Boolean(uncertainAction) || acceptFee.previewing;

  // [MCV-1646] The editor opens immediately in draft mode; no request is made
  // until Submit Offer. `startFirstNegotiation` maps straight onto
  // `startDraftIfEmpty`.
  const handleCounter = useCallback(
    (responseId: number, options?: { startFirstNegotiation: boolean }) => {
      if (actionsBlocked) return;
      setNegotiationStartDraftIfEmpty(options?.startFirstNegotiation ?? false);
      setNegotiationResponseId(responseId);
    },
    [actionsBlocked]
  );

  const handleNegotiationClose = useCallback(() => {
    setNegotiationResponseId(null);
    handleReload();
  }, [handleReload]);

  return (
    <B2BInquiryApplyFlow onApplied={handleReload}>
      {({ open: openApply, blocked: applyBlocked }) => (
        <div className="flex min-h-0 flex-1 flex-col" data-cy="b2b-job-opportunities">
          <OpportunityRecoveryNotice
            action={uncertainAction}
            busy={loading || messagesLoading || writing || applyBlocked}
            canAcknowledge={canAcknowledgeUncertainty}
            onReview={() => {
              handleReviewUncertainty();
              setDetailOpen(true);
            }}
            onAcknowledge={handleAcknowledgeUncertainty}
            onDiscard={handleDiscardUncertainty}
          />
          {error && (
            <div
              className="mb-4 p-3 bg-error-50 text-white text-sm rounded-md"
              data-cy="b2b-opportunities-error"
            >
              {error}
            </div>
          )}

          <MarketWorkspaceGrid
            dataCy="b2b-job-opportunities-grid"
            detailKey={selectedOpportunity?.responseId}
            drillDown={side ? { detailOpen, onBack: () => setDetailOpen(false) } : undefined}
            sidebar={
              <OpportunitiesSidebar
                opportunities={opportunities}
                selectedResponseId={selectedOpportunity?.responseId}
                filter={filter}
                includeDeclined={includeDeclined}
                loading={loading}
                title={title ?? 'B2B Opportunities'}
                responsiveFilters={side != null}
                onSelect={handleSelect}
                onFilter={handleFilter}
                onIncludeDeclined={handleIncludeDeclined}
                renderOpportunity={(opportunity, isSelected, onSelect) => (
                  <OpportunityCard
                    opportunity={opportunity}
                    isSelected={isSelected}
                    onSelect={onSelect}
                  />
                )}
              />
            }
            detail={
              selectedOpportunity ? (
                <OpportunityDetail
                  opportunity={selectedOpportunity}
                  onAccept={onAccept}
                  onCounter={handleCounter}
                  onWithdraw={handleWithdraw}
                  onApply={() =>
                    openApply({
                      responseId: selectedOpportunity.responseId,
                      listingId: selectedOpportunity.listing.id,
                      title: selectedOpportunity.listing.listingTitle,
                    })
                  }
                  actionsDisabled={actionsBlocked || applyBlocked}
                  pinActions={side != null}
                  identityControls={
                    <ResponseUnmaskPanel
                      responseId={selectedOpportunity.responseId}
                      viewerScopeKey={viewerScopeKey}
                      refreshKey={`${selectedOpportunity.updatedAt}:${messages[messages.length - 1]?.id ?? ''}`}
                      disabled={actionsBlocked || applyBlocked}
                      onChanged={handleReload}
                    />
                  }
                />
              ) : (
                <MarketContentPanel className="h-full" variant="empty">
                  {loading ? (
                    <MarketLoadingPanel label="Loading opportunities" />
                  ) : (
                    <MarketEmptyPanel title="Select an opportunity to view details" />
                  )}
                </MarketContentPanel>
              )
            }
            conversation={
              selectedOpportunity ? (
                <ResponseDetail
                  key={selectedOpportunity.responseId}
                  opportunity={selectedOpportunity}
                  messages={messages}
                  messagesLoading={messagesLoading}
                  sendingMessage={sendingMessage}
                  actionsDisabled={applyBlocked || actionsBlocked}
                  sendReceipt={sendReceipt}
                  initialDraft={
                    retainedReply?.messageBody ??
                    (uncertainAction?.responseId === selectedOpportunity.responseId
                      ? uncertainAction.messageBody
                      : undefined)
                  }
                  onDraftChange={handleEditReply}
                  replyError={replyError}
                  currentUserInfoId={userInfoId}
                  onSendMessage={handleSendMessage}
                />
              ) : (
                <MarketContentPanel className="h-full" variant="empty">
                  <MarketEmptyPanel title="No opportunity selected" />
                </MarketContentPanel>
              )
            }
          />
          <ResponseNegotiationModal
            open={negotiationResponseId != null}
            responseId={negotiationResponseId}
            responseType="msaas"
            viewerRole="EMPLOYER"
            listingSide={negotiationOpportunity?.listingSide}
            listingFilled={
              negotiationOpportunity != null && isListingFilled(negotiationOpportunity)
            }
            startDraftIfEmpty={negotiationStartDraftIfEmpty}
            onDraftSubmitted={handleReload}
            onClose={handleNegotiationClose}
          />
          <ActivationFeeDialog {...acceptFee.dialogProps} />
        </div>
      )}
    </B2BInquiryApplyFlow>
  );
};
